import {
  CompletePathItemDocument,
  ResetPathItemDocument
} from "@pathwright/web/src/modules/api/generated"
import { getApolloClient } from "@pathwright/web/src/modules/pathwright/PathwrightClient"
import {
  PATH_ITEM_COMPLETED_EVENT,
  dispatchGlobalEvent
} from "@pathwright/web/src/modules/utils/dispatcher"
import { captureException } from "@sentry/browser"
import isEqual from "react-fast-compare"
import { serializeItemForState } from "../../library/show/components/path/state/utils"

const PathItem = require("library/models/path-item").default
const StepCompletion = require("path/models/step-completion").default
const DownloadCollection =
  require("path/downloads/models/download-collection").default

class StepStore extends require("lib/static-shim").default(
  require("lib/core/store/backbone-store").default
) {
  static initClass() {
    this.prototype.resources = {
      completion_response: {
        model: require("path/models/feedback-step-completion").default
      },
      step: {
        model: PathItem
      },
      content: {
        model: require("content/lib/models/content").default({})
      },
      // assessment needs it's own resource since the model varies (uses same keys as content though)
      assessment: {
        model: require("content/assessment/models/assessment").default,
        cache: false,
        keys: {
          resourceKey: "content",
          loadingKey: "content_loading",
          errorKey: "content_error"
        }
      },
      assessment_for_user: {
        model: require("assessments/models/assessment-for-user").default,
        cache: false,
        keys: {
          resourceKey: "content",
          loadingKey: "content_loading",
          errorKey: "content_error"
        }
      },
      reset_assessment_for_user: {
        model: require("assessments/models/assessment-for-user").default,
        cache: false
      },
      downloads: {
        collection: DownloadCollection
      },
      notes: {
        collection: require("note/models/note-collection").default
      }
    }

    this.prototype.storeTriggers = {
      "set:step"() {
        return this.get("step")
      },
      "update:step"() {
        return this.get("step")
      }
    }

    this.prototype.storeTriggerHandlers = {
      notification: {
        "notification:received"(notification) {
          if (!this.get("step")) {
            return
          }
          // If a step-based notification was recieved
          // reload the step if it belongs to this path
          if (notification.context_data != null) {
            const { step, discussion, response } = notification.context_data
            if (
              !response &&
              step === __guard__(this.get("step"), (x) => x.sourceID())
            ) {
              // new discussion posted to this step
              // TODO: reload discussions queries
            }
          }
        }
      },
      stepDownloadsAdmin: {
        "download:added"(download) {
          return this.action.addDownload(download)
        },
        "download:deleted"(download) {
          return this.action.removeDownload(download)
        }
      },
      path: {
        "path:item:reset"(item) {
          if (item.id === __guard__(this.get("step"), (x) => x.id)) {
            return this.action.updateStep(item)
          }
        },
        "path:loaded"(path) {
          const step = this.get("step")
          if (step) {
            // Reseting step after path load (possibly stale from a previously loaded path, say after user registers, or after sync)
            const stepSourceID = step.get("sync_source_id")
            const replacementStep =
              App.getStore("path").request.findItem(stepSourceID)

            if (path.id !== step.get("path_id")) {
              if (replacementStep) {
                App.getStore("step").action.setSelectedStepByID(
                  replacementStep.id,
                  true // forceSet
                )
              }

              this.action.loadStep()
            }
          }
        },
        "path:step:cleared"() {
          this.reset()
        },
        "path:step:selected"() {
          const { step: stepStoreStep } = App.getStore("step").getState()
          const { selected_step: pathStoreStep } =
            App.getStore("path").getState()

          if (
            stepStoreStep &&
            pathStoreStep &&
            stepStoreStep.id === pathStoreStep.id &&
            !isEqual(stepStoreStep, pathStoreStep)
          ) {
            // force updating selected step when content has changed
            App.getStore("step").action.setSelectedStepByID(
              pathStoreStep.id,
              true /* forceSet */
            )
          }
        }
      },
      contentAdmin: {
        "content:updated"() {
          // force reload the step content to ensure we have latest content data
          this.action.loadContent({ forceReload: true })
        }
      },
      auth: {
        "user:auth:changed"() {
          return this.resource.content.clearCache()
        }
      }
    }

    this.prototype.actions = {
      updateStep(data) {
        this.get("step").set(data)
        if (data.completion) {
          this.get("completion").set(data.completion)
        }
        return this.triggerChange("change:step")
      },

      setStep(stepData, forceSet) {
        if (_.isFunction(stepData.toJSON)) {
          stepData = stepData.toJSON()
        }

        if (
          !forceSet &&
          stepData.id === __guard__(this.get("step"), (x) => x.id)
        ) {
          return
        }

        // TODO: make sure removing this doesn't cause any issues.
        // must clear step now, allowing UI to rerender (not ideal)
        // this.action.clearStep()

        const step = new PathItem(stepData)
        const update = {}
        update.step = step
        update.comment =
          stepData.completion != null
            ? stepData.completion.completion_comment
            : undefined
        update.group = this.getStore("path").request.getItemByID(
          step.get("parent")
        )
        // Clear out error keys.
        update.step_completion_error = null
        update.content_error = null
        if (update.group) {
          update.active_step_index = this.request.getStepIndex(
            update.group.serialize().items,
            step
          )
        }

        const completionData = _.assign(stepData.completion, {
          step_id: step.id,
          path_id: this.getStore("path").get("path").id
        })
        update.completion = new StepCompletion(completionData)

        this._mutate(update)

        if (stepData.permissions.can_view_content) {
          this.action.loadContent()
        }

        return this.storeTrigger("set:step")
      },

      loadStep(forceReload) {
        if (forceReload == null) {
          forceReload = false
        }
        const { step } = this.getState()
        const params = {
          path_id: step.path_id,
          sync_source_id: step.sync_source_id
        }

        return this.resource.step.load(params, forceReload).then(() => {
          this.action.setStep(this.get("step"))
          return this.storeTrigger("update:step")
        })
      },

      clearStep(preserveGroup = true) {
        if (__guard__(this.get("step"), (x) => x.id) != null) {
          // we don't want to clear the group, step while in transition
          let keys = _.keys(this.attributes)
          if (preserveGroup) {
            keys = _.without(keys, "group", "step")
          }
          const clearMap = {}
          for (let key of Array.from(keys)) {
            clearMap[key] = null
          }
          return this._mutate(clearMap)
        }
      },

      setSelectedStepByID(stepID, forceSet) {
        stepID = parseInt(stepID)
        if (stepID !== __guard__(this.get("step"), (x) => x.id) || forceSet) {
          const step = this.getStore("path").request.findStep(stepID)
          if (step) {
            return this.action.setStep(step, forceSet)
          }
        }
      },

      loadAncillaryContent() {
        const { step } = this.getState()
        if (step.permissions.can_view_content) {
          return this.action.loadDownloads()
        }
      },

      loadContent(options) {
        const { forceReload, ignoreCache } = options || {
          forceReload: false,
          ignoreCache: false
        }
        const { step } = this.getState()

        if (!step.permissions.can_view_content) {
          return Promise.reject("permission denied")
        }

        const content = this.get("step").get("content")

        // if content?.method is "blocks"
        //   console.log("found some blocks content!")
        //   return

        if (content?.id) {
          const params = {
            id: content.id,
            step_id: this.get("step").id,
            type: content.type
          }
          return this.resource.content
            .load(params, forceReload, ignoreCache)
            .then((content) => {
              if (content == null) {
                throw new Error("no content")
              }
              if (
                content?.id &&
                content.get("type") === "assessment" &&
                content.get("method") === "assessment_editor"
              ) {
                // @resource.content and @resource.assessment_for_user share the same keys
                // and loading the user assessment will overwrite the @resource.content since
                // we're not caching the user assessment.
                return this.action.loadAssessmentForUser().promise
              } else {
                return content
              }
            })
            .then((content) => {
              if (content?.id) {
                content.set("step_id", params.step_id)
              }
              return this.action.loadAncillaryContent()
            })
            .catch(function (err) {
              console.error("error loading content", err)
              return console.trace()
            })
        } else {
          // step doesn't have any content but we still need to load other stuff
          return this.action.loadAncillaryContent()
        }
      },

      loadAssessmentForUser() {
        // hack for resetting assessment
        if (this.get("reset_assessment_for_user")) {
          return this._mutate({
            content: this.get("reset_assessment_for_user")
          })
          // TODO: not capable of previewing assessment as it is dependent on a user
        } else if (App.user.id) {
          // Loads a full assessment built with the assessment editor. Has to be
          // loaded seperately from normal content since it has a specialized model
          const params = {
            id: this.get("content").id,
            stepID: this.get("step").id,
            userID: window.App.user.id
          }
          return this.resource.assessment_for_user.load(params)
        }
      },

      loadResetAssessmentForUser() {
        const params = {
          id: this.get("content").id,
          stepID: this.get("step").id,
          userID: window.App.user.id
        }
        return this.resource.reset_assessment_for_user.load(params)
      },

      clearContent() {
        return this._mutate({ content: null })
      },

      setCompletionData(data) {
        const merge = _.assign(
          {},
          __guard__(this.get("completion"), (x) => x.attributes),
          data
        )
        const completion = new StepCompletion(merge)
        if (this.get("group") != null) {
          __guard__(this.get("group").items.get(data.step), (x1) =>
            x1.set("completion", merge)
          )
        }
        this._mutate({
          completion
        })
        const { step } = this.getState()
        this.action.updateStep({
          ...step,
          completion: merge
        })
      },

      async completeStep(skip = false, completionToken) {
        const { step, comment } = this.getState()
        const completion = this.get("completion")

        if (!completion.get("is_complete")) {
          this._mutate({
            is_completing_step: true,
            is_skipping_step: skip,
            step_completion_error: null
          })

          const client = getApolloClient(window.school.id)
          try {
            const {
              data: { completePathItem }
            } = await client.mutate({
              variables: {
                id: step.id,
                path_id: step.path_id,
                skip,
                completion_data: {
                  ...completion.toJSON(),
                  completion_token: completionToken,
                  completion_comment: comment
                }
              },
              mutation: CompletePathItemDocument
            })

            // Potentially the step has been cleared or switched (user navigated away),
            // so ensuring we're still dealing with the completed step to avoid any unnecessary errors.
            if (this.getState().step?.id === step?.id) {
              const { offering, resource } = App.getStore("resource").getState()
              const nextItem = serializeItemForState({
                item: completePathItem,
                offering,
                resource
              })
              const nextCompletion = nextItem.completion

              // dispatch the path:item:completed event
              try {
                if (nextCompletion.progress === 1) {
                  const { user } = App.getStore("auth").getState()
                  const { resource, offering } =
                    App.getStore("resource").getState()
                  const { step } = this.getState()

                  const data = {
                    user: {
                      id: user.id,
                      email: user.email,
                      firstName: user.first_name,
                      lastName: user.last_name,
                      fullName: user.full_name
                    },
                    pathItem: {
                      id: step.id,
                      name: step.name,
                      sourceId: step.sync_source_id,
                      points: nextCompletion.userpoints_value,
                      pointsAvailable: nextCompletion.userpoints_earned
                    },
                    cohort: {
                      id: offering.id,
                      name: offering.name
                    },
                    resource: {
                      id: resource.id,
                      name: resource.name
                    }
                  }

                  dispatchGlobalEvent(PATH_ITEM_COMPLETED_EVENT, data)
                }
              } catch (error) {
                console.warn(
                  `Failed to dispatch event "${PATH_ITEM_COMPLETED_EVENT}"`,
                  error
                )
              }

              this.action.setCompletionData(nextCompletion)
              this._updatePathProgress(nextItem)
              // legacy content below; blocks content resets itself and handles completion disabled state
              if (
                this.get("content") != null &&
                // Hack Alert: oddly there appear to be times where the content is not null
                // but the content is also not a backbone model, or at least, "get" is not a function.
                // Since this the following code is only meant for legacy content, let's just
                // side-step when "get" is not a function.
                typeof this.get("content").get === "function" &&
                this.get("content").get("type") !== "blocks"
              ) {
                if (completion.get("has_failed_passing_attempt")) {
                  this.get("content").reset && this.get("content").reset()
                }
                if (
                  this.get("content").get("type") === "assessment" &&
                  this.get("content").get("method") === "assessment_editor"
                ) {
                  await this.action.loadAssessmentForUser()
                }
              }
            }
          } catch (error) {
            captureException(error)
            this._mutate({ step_completion_error: error })
          } finally {
            this._mutate({
              is_completing_step: false,
              is_skipping_step: false
            })
          }
        }
      },

      skipStep(completionToken) {
        return this.action.completeStep(true /* skip */, completionToken)
      },

      resetAssessment() {
        const { completion } = this.getState()
        if (completion.is_complete) {
          return this.action.loadResetAssessmentForUser().promise.then(() => {
            for (let answer of Array.from(
              this.get("reset_assessment_for_user").get("assessment").answers
            )) {
              answer.is_answered = false
              answer.is_answered_correctly = false
            }
            return this.action.resetStep()
          })
        }
      },

      async resetStep() {
        const { step } = this.getState()
        const content = this.get("content")
        let completion = this.get("completion")
        if (completion.get("is_complete")) {
          this._mutate({ is_reseting_completion: true })

          const client = getApolloClient(window.school.id)
          const {
            data: { resetPathItem }
          } = await client.mutate({
            variables: {
              id: step.id,
              path_id: step.path_id
            },
            mutation: ResetPathItemDocument
          })

          const { offering, resource } = App.getStore("resource").getState()
          const nextItem = serializeItemForState({
            item: resetPathItem,
            offering,
            resource
          })
          const nextCompletion = nextItem.completion
          this.action.setCompletionData(nextCompletion)
          this._updatePathProgress(nextItem)
          this._mutate({ is_reseting_completion: false })

          // Also reset content if necessary
          if (content != null && content.get("type") === "assessment") {
            if (content.get("method") === "assessment_editor") {
              // Need to reload
              return this.action.loadContent({ ignoreCache: true })
            }
          }
        }
      },

      loadDownloads(forceReload) {
        if (forceReload == null) {
          forceReload = false
        }
        const { step } = this.getState()
        const pathID = step.path_id
        const stepID = step.sync_source_id
        return this.action.loadDownloadsByID(pathID, stepID, forceReload)
      },

      loadDownloadsByID(pathID, sourceID, forceReload) {
        if (forceReload == null) {
          forceReload = false
        }
        const options = {
          path: pathID,
          step: sourceID
        }
        return this.resource.downloads.list(options, forceReload)
      },

      removeDownload(download) {
        const downloads = this.get("downloads")
        downloads.remove(download.id)
        this.resource.downloads.clearCache()
        this._mutate({ downloads })
        return this.triggerChange("change:downloads")
      },

      addDownload(download) {
        const downloads = this.get("downloads")
        downloads.add(download)
        this.resource.downloads.clearCache()
        this._mutate({ downloads })
        return this.triggerChange("change:downloads")
      },

      loadNotes(forceReload) {
        if (forceReload == null) {
          forceReload = false
        }
        const { step } = this.getState()
        const ctx = this.getStore("note").request.getContextStr(
          step.sync_source_id
        )
        return this.resource.notes.list({ context: ctx }, forceReload)
      },

      changeComment(comment) {
        return this._mutate({ comment })
      },

      triggerStepChange() {
        return this.triggerChange("change:step")
      }
    }

    this.prototype.requests = {
      canSeeContent() {
        const isAuthenticated = this.getStore("auth").request.isAuthenticated()
        const allowPreview =
          isAuthenticated || this.get("step").get("allow_preview") === true
        return isAuthenticated || allowPreview
      },

      getStepIndex(items, step) {
        return _.findIndex(items, (s) => s.id === step.id)
      },

      getNextItem() {
        // Note this does not return a model!
        let nextItem = null
        const { active_step_index, group } = this.getState()
        if (active_step_index < group.items.length - 1) {
          nextItem = group.items[active_step_index + 1]
        } else {
          nextItem =
            __guard__(
              this.getStore("path").request.getNextGroup(group.id),
              (x) => x.serialize()
            ) || null
        }
        return nextItem
      },

      getNextStep() {
        // returns step object or undefined
        const { active_step_index, group } = this.getState()
        return group.items[active_step_index + 1]
      },

      getPrevStep() {
        // returns step object or undefined
        const { active_step_index, group } = this.getState()
        return group.items[active_step_index - 1]
      },
      getStepHasLegacyContent() {
        const { content } = this.getState()
        return content && content.type !== "blocks"
      }
    }
  }

  defaults() {
    return {
      step: null, // the step model
      group: null, // the group the step is in
      completion: null, // the user's completion
      content: null, // the steps content model (null until fetched)
      notes: null, // notes for this step
      downloads: null, // downloads for this step
      comment: null, // student comment w/ submission

      // UI states
      active_step_index: null,
      is_skipping_step: false,
      is_reseting_completion: false,
      step_completion_error: null,
      notes_loading: false,
      content_loading: null,
      content_error: null
    }
  }

  _updatePathProgress(step) {
    const pathStore = window.App.stores.requestStore("path")
    return pathStore.action.handleProgressAfterStepCompleteAction(step)
  }

  computeURLForState() {
    ;`\
Returns a URL matching the user's existing state within the step\
`
    const pathStore = window.App.stores.requestStore("path")
    const baseURL = pathStore.computeURLForState()
    // TODO: step routing
    return baseURL
  }
}
StepStore.initClass()

export default window.App.stores.registerStore("step", StepStore)

function __guard__(value, transform) {
  return typeof value !== "undefined" && value !== null
    ? transform(value)
    : undefined
}
